import React from 'react';
import './i18n';
import './index.css';
import './brandKits/whiteLabel.css';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import { PreferencesProvider } from './context/PreferencesContext';
import { BrandKitProvider } from './context/BrandKitContext';
import { HelmetProvider } from 'react-helmet-async';
import reportWebVitals from './reportWebVitals';
import { reconcileDemoSession } from './components/DemoModeBar';
import { mountApplication } from './utils/mountApplication';
import { applyPortalDocumentMetadata } from './utils/portalDocumentMetadata';
import { isRuntimeIdentityRoute } from './utils/runtimeIdentityRoute';

applyPortalDocumentMetadata();

// Roll back any demo impersonation left in localStorage after the browser was
// closed mid-demo (the demo session in sessionStorage is gone, but the demo
// user in localStorage would otherwise persist). Must run before render/routing.
reconcileDemoSession();

// Suppress Google Maps' "Using the alpha channel of the Google Maps JavaScript
// API. For development purposes only." banner. We legitimately need v=alpha
// because the Prospecting page uses Map3DElement / Photorealistic 3D Tiles
// from the maps3d library, which is alpha-only. Google injects this notice as
// a fixed banner at the top of the document; it's not part of any map and is
// safe to hide. We watch document.body for added nodes and prune anything
// matching the banner's exact text/structure. Idempotent - survives Google
// re-injecting it after route changes.
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const ALPHA_NOTICE_TEXT = 'Using the alpha channel of the Google Maps JavaScript API';
    const removeAlphaBanner = (root) => {
        if (!root) return;
        // Find every text node that mentions the alpha channel notice. The
        // banner has TWO children (warning text + a Dismiss link/button) so
        // the natural single-child walk-up stops too soon and leaves a
        // visible empty banner shell with the Dismiss link. To remove the
        // whole banner, we walk all the way up to the top-level wrapper -
        // either a fixed/sticky-positioned ancestor (Google's injected
        // banner is fixed at top of page) or the highest ancestor that is
        // still a direct child of <body>.
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        let node;
        const toRemove = new Set();
        while ((node = walker.nextNode())) {
            if (!node.nodeValue || !node.nodeValue.includes(ALPHA_NOTICE_TEXT)) continue;
            let target = node.parentElement;
            let bestAncestor = target;
            while (target && target !== document.body && target !== document.documentElement) {
                let pos = '';
                try { pos = window.getComputedStyle(target).position; } catch (_) { /* noop */ }
                if (pos === 'fixed' || pos === 'sticky') {
                    bestAncestor = target;
                    break;
                }
                if (target.parentElement === document.body) {
                    bestAncestor = target;
                    break;
                }
                bestAncestor = target;
                target = target.parentElement;
            }
            if (bestAncestor && bestAncestor !== document.body && bestAncestor !== document.documentElement) {
                toRemove.add(bestAncestor);
            }
        }
        toRemove.forEach((el) => { try { el.remove(); } catch (_) { /* noop */ } });
    };
    const startObserver = () => {
        if (!document.body) return;
        removeAlphaBanner(document.body);
        const obs = new MutationObserver((mutations) => {
            for (const m of mutations) {
                m.addedNodes.forEach((n) => {
                    if (n.nodeType === 1) removeAlphaBanner(n);
                });
            }
        });
        obs.observe(document.body, { childList: true, subtree: true });
    };
    if (document.body) {
        startObserver();
    } else {
        document.addEventListener('DOMContentLoaded', startObserver, { once: true });
    }
}

const rootElement = document.getElementById('root');
const runtimeIdentityEntry = isRuntimeIdentityRoute(window.location.pathname);

const tree = (
  <React.StrictMode>
    <ErrorBoundary>
      <HelmetProvider>
        {runtimeIdentityEntry ? <App /> : <PreferencesProvider>
          <BrandKitProvider>
            <App />
          </BrandKitProvider>
        </PreferencesProvider>}
      </HelmetProvider>
    </ErrorBoundary>
  </React.StrictMode>
);

mountApplication(rootElement, tree);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Offline inspections: register the service worker (app shell caching so the
// app opens with no signal) and start the background sync engine that replays
// queued inspections when connectivity returns. The SW is only registered in
// production builds - CRA's dev server + a custom SW cause stale-asset issues.
if (typeof window !== 'undefined' && !runtimeIdentityEntry) {
    if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
        window.addEventListener('load', () => {
            navigator.serviceWorker
                .register('/inspection-sw.js')
                .catch((err) => console.warn('SW registration failed:', err));
        });
    }
    import('./offline/inspectionSync')
        .then(({ startInspectionSync }) => startInspectionSync())
        .catch(() => {});
}
